import type { components } from "@roastery/api-client";
import { useApi } from "@roastery/auth";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";

export type LabelTemplate = components["schemas"]["LabelTemplate"];
export type LabelElement = components["schemas"]["LabelElement"];
export type LabelFieldOption = components["schemas"]["LabelFieldOption"];
export type LabelSize = components["schemas"]["LabelSize"];
export type LabelWarning = components["schemas"]["LabelWarning"];
export type SaveLabelTemplate = components["schemas"]["SaveLabelTemplate"];
// springdoc leaves record components optional in the schema, hence the NonNullable wrappers.
export type LabelFieldCode = NonNullable<LabelElement["field"]>;
export type LabelKind = NonNullable<LabelTemplate["kind"]>;
export type LabelSizePreset = NonNullable<LabelTemplate["sizePreset"]>;
export type LabelTextSize = NonNullable<LabelElement["size"]>;
export type BlendCompositionSource = NonNullable<LabelElement["source"]>;

const TEMPLATES_PATH = "/api/v1/labels/templates";
const TEMPLATE_PATH = "/api/v1/labels/templates/{id}";
const EFFECTIVE_PATH = "/api/v1/labels/templates/effective";

export const TEXT_SIZES: readonly LabelTextSize[] = ["S", "M", "L"];
export const COMPOSITION_SOURCES: readonly BlendCompositionSource[] = ["ACTUAL", "RECIPE"];

export function useLabelTemplates(kind?: LabelKind, includeArchived = false) {
  const api = useApi();
  return api.query.useQuery("get", TEMPLATES_PATH, {
    params: { query: { kind, includeArchived } },
  });
}

export function useLabelTemplate(id: string) {
  const api = useApi();
  return api.query.useQuery("get", TEMPLATE_PATH, { params: { path: { id } } });
}

/** The template one product prints with: its own, or the roastery's default (204 → undefined). */
export function useTemplateForProduct(productId: string) {
  const api = useApi();
  return api.query.useQuery("get", EFFECTIVE_PATH, { params: { query: { productId } } });
}

/**
 * The same question asked from the shelf: which template will this bag print with. The printing dialog
 * knows a packed row and not the product behind it, and needs the answer before the first press so it
 * can show what the template is missing instead of reporting it after the file has downloaded.
 */
export function useTemplateForPackedRow(packagedStockId: string | undefined) {
  const api = useApi();
  return api.query.useQuery(
    "get",
    EFFECTIVE_PATH,
    { params: { query: { packagedStockId } } },
    { enabled: Boolean(packagedStockId) },
  );
}

export function useLabelFields() {
  const api = useApi();
  return api.query.useQuery("get", "/api/v1/labels/fields", {});
}

export function useLabelSizes() {
  const api = useApi();
  return api.query.useQuery("get", "/api/v1/labels/sizes", {});
}

/** Every mutation invalidates the list, the editor and the "which label does this product print with". */
export function useInvalidateLabelTemplates() {
  const queryClient = useQueryClient();
  return () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ["get", TEMPLATES_PATH] }),
      queryClient.invalidateQueries({ queryKey: ["get", TEMPLATE_PATH] }),
      queryClient.invalidateQueries({ queryKey: ["get", EFFECTIVE_PATH] }),
    ]);
}

export function useCreateLabelTemplate() {
  const api = useApi();
  const invalidate = useInvalidateLabelTemplates();
  return api.query.useMutation("post", TEMPLATES_PATH, { onSuccess: () => void invalidate() });
}

export function useUpdateLabelTemplate() {
  const api = useApi();
  const invalidate = useInvalidateLabelTemplates();
  return api.query.useMutation("put", TEMPLATE_PATH, { onSuccess: () => void invalidate() });
}

export function useArchiveLabelTemplate() {
  const api = useApi();
  const invalidate = useInvalidateLabelTemplates();
  return api.query.useMutation("delete", TEMPLATE_PATH, { onSuccess: () => void invalidate() });
}

export function useSetDefaultLabelTemplate() {
  const api = useApi();
  const invalidate = useInvalidateLabelTemplates();
  return api.query.useMutation("post", "/api/v1/labels/templates/{id}/default", {
    onSuccess: () => void invalidate(),
  });
}

/**
 * The preview PDF, as an object URL. Fetched rather than linked because the endpoint is a POST with
 * the unsaved layout in the body and needs the bearer token; the caller revokes the URL when it
 * replaces it.
 */
export function usePreviewLabel() {
  const api = useApi();
  return async (command: SaveLabelTemplate, lang?: string) => {
    const { data, error } = await api.fetch.POST("/api/v1/labels/templates/preview", {
      params: { query: { lang } },
      body: command,
      parseAs: "blob",
    });
    if (error || !data) {
      throw error ?? new Error("preview failed");
    }
    return URL.createObjectURL(data as Blob);
  };
}

// --- printing (US-24.2) --------------------------------------------------------------------------

export type LabelPrintJob = components["schemas"]["LabelPrintJob"];
export type PrintJobLine = components["schemas"]["PrintJobLine"];
export type LabelFormat = NonNullable<LabelPrintJob["format"]>;
export type LabelSourceType = NonNullable<LabelPrintJob["sourceType"]>;

const PRINT_JOBS_PATH = "/api/v1/labels/print-jobs";

export const LABEL_FORMATS: readonly LabelFormat[] = ["PDF", "ZPL"];

export function usePrintJobs(sourceType: LabelSourceType, sourceId: string, enabled = true) {
  const api = useApi();
  return api.query.useQuery(
    "get",
    PRINT_JOBS_PATH,
    { params: { query: { sourceType, sourceId } } },
    { enabled: enabled && Boolean(sourceId) },
  );
}

export function useInvalidatePrintJobs() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: ["get", PRINT_JOBS_PATH] });
}

/**
 * Prints, then fetches the file (US-24.2). Two calls because that is what the API does: the first one
 * records the print and answers with its warnings, the second hands over the bytes — and the second is
 * also what "print this again" from the history calls, so a reprint cannot differ from the original.
 */
export function usePrintLabels() {
  const api = useApi();
  const invalidate = useInvalidatePrintJobs();
  return async (body: {
    sourceType: LabelSourceType;
    sourceId: string;
    templateId?: string;
    format: LabelFormat;
    lines?: { packagedStockId: string; copies: number }[];
  }) => {
    const { data, error } = await api.fetch.POST("/api/v1/labels/print", { body });
    if (error || !data) {
      throw error ?? new Error("print failed");
    }
    void invalidate();
    return data as LabelPrintJob;
  };
}

/** The file of one print, as a Blob — the same call for a fresh print and for a reprint. */
export function usePrintJobFile() {
  const api = useApi();
  return async (jobId: string) => {
    const { data, error } = await api.fetch.GET("/api/v1/labels/print-jobs/{id}/file", {
      params: { path: { id: jobId } },
      parseAs: "blob",
    });
    if (error || !data) {
      throw error ?? new Error("file failed");
    }
    return data as Blob;
  };
}

// --- internal codes and stickers (US-24.3) ---------------------------------------------------------

export type ResolvedCode = components["schemas"]["ResolvedCode"];
export type InternalCodeType = NonNullable<ResolvedCode["type"]>;
/** One button on the scan sheet (US-33.2), and which action it is. */
export type ScanAction = components["schemas"]["ScanAction"];
export type ScanActionCode = NonNullable<ScanAction["code"]>;

/**
 * The code image beside a number. A plain authenticated fetch rather than an `<img src>`: the endpoint
 * needs the bearer token, and the browser would send none.
 */
export function useCodeImage(type: InternalCodeType, id: string | undefined, qr = false) {
  const api = useApi();
  return api.query.useQuery(
    "get",
    "/api/v1/labels/codes/{type}/{id}.png",
    { params: { path: { type, id: id ?? "" }, query: { symbology: qr ? "QR" : "CODE128" } }, parseAs: "blob" },
    { enabled: Boolean(id) },
  );
}

/**
 * What a scanned or pasted code names, and what can be done with it. Called on demand, not as a query.
 *
 * <p>Stable across renders on purpose (US-33.2): a function rebuilt on every render would restart
 * every effect and every loop that holds one.
 */
export function useResolveCode() {
  const api = useApi();
  return useCallback(
    async (code: string) => {
      const { data, error } = await api.fetch.GET("/api/v1/labels/codes/resolve", {
        params: { query: { code } },
      });
      if (error || !data) {
        throw error ?? new Error("unresolved");
      }
      return data as ResolvedCode;
    },
    [api],
  );
}

/**
 * The same lookup as a query (US-33.2), for the scan sheet: the code is a value the screen holds, and
 * "what is this" is a fetch about that value — which is a query, not something an effect should be
 * kicking off by hand.
 *
 * <p>`retry: false` because the interesting failure is a 404: an unknown code is an answer, and
 * asking three more times only delays telling the person so.
 */
export function useCodeLookup(code: string, attempt = 0) {
  const resolve = useResolveCode();
  return useQuery({
    // `attempt` is in the key and in nothing else (US-33.3). Four bags of the same SKU are four
    // scans of the same string, and a query keyed by the string alone would answer the first and
    // then sit still — so each accepted scan bumps a counter, which is a new key, which is a new
    // answer. The counter moves in the event handler that read the code, never in an effect.
    queryKey: ["scan", attempt, code],
    queryFn: () => resolve(code),
    enabled: code.trim() !== "",
    retry: false,
    gcTime: 0,
    staleTime: 0,
  });
}

export function usePrintSticker() {
  const api = useApi();
  const invalidate = useInvalidatePrintJobs();
  return async (body: { type: InternalCodeType; id: string; copies?: number; format: LabelFormat }) => {
    const { data, error } = await api.fetch.POST("/api/v1/labels/codes/stickers", { body });
    if (error || !data) {
      throw error ?? new Error("sticker failed");
    }
    void invalidate();
    return data as LabelPrintJob;
  };
}

// --- the public page under the QR (US-24.4) --------------------------------------------------------

export type TraceSettings = components["schemas"]["TraceSettings"];
export type SaveTraceSettings = components["schemas"]["SaveTraceSettings"];
export type PublicTrace = components["schemas"]["PublicTrace"];

const TRACE_SETTINGS_PATH = "/api/v1/labels/trace-settings";

export function useTraceSettings() {
  const api = useApi();
  return api.query.useQuery("get", TRACE_SETTINGS_PATH, {});
}

export function useSaveTraceSettings() {
  const api = useApi();
  const queryClient = useQueryClient();
  return api.query.useMutation("put", TRACE_SETTINGS_PATH, {
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["get", TRACE_SETTINGS_PATH] }),
  });
}

/** The public page as a consumer would see it — through the token, and without counting as a view. */
export function useTracePreview(code: string | undefined, lang: string | undefined, enabled: boolean) {
  const api = useApi();
  return api.query.useQuery(
    "get",
    "/api/v1/labels/trace-codes/{code}/preview",
    { params: { path: { code: code ?? "" }, query: { lang } } },
    { enabled: enabled && Boolean(code) },
  );
}
